/*
  Scrollable click-to-zoom lightbox for the product gallery (Figma: no lightbox
  chrome). Clicking an image opens a full-screen white overlay with ALL gallery
  images stacked vertically — each scaled as large as it fits the viewport —
  scrolled to the clicked one. Scroll to move through the rest; click anywhere
  (or Esc) closes. No close button.

  Loading: each slot shows the already-decoded gallery candidate instantly in a
  fixed-geometry box (--bott-zoom-ar), then swaps to the width=1946 version in
  place as it nears the viewport (IntersectionObserver); hovering the gallery
  preloads the hovered image's zoom size ahead of the click.

  It replaces Dawn's product-media-modal for images only: a capture-phase click
  listener on the <media-gallery> runs before Dawn's modal-opener button
  listener and stops the event, so the full media modal never opens. Non-image
  media (video/model) fall through to Dawn's behaviour untouched.
*/
if (!customElements.get('bott-image-zoom')) {
  customElements.define(
    'bott-image-zoom',
    class BottImageZoom extends HTMLElement {
      connectedCallback() {
        this.gallery = this.closest('media-gallery');
        if (!this.gallery) return;
        this.onGalleryClick = this.onGalleryClick.bind(this);
        this.onGalleryPointerOver = this.onGalleryPointerOver.bind(this);
        this.onOverlayClick = this.onOverlayClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.gallery.addEventListener('click', this.onGalleryClick, true);
        // Hover intent: start fetching the zoom-size image before the click.
        this.gallery.addEventListener('pointerover', this.onGalleryPointerOver, { passive: true });
      }

      disconnectedCallback() {
        if (this.gallery) {
          this.gallery.removeEventListener('click', this.onGalleryClick, true);
          this.gallery.removeEventListener('pointerover', this.onGalleryPointerOver);
        }
        this.close(true);
      }

      zoomUrl(image) {
        // Bump the chosen srcset candidate to the largest gallery width so the
        // full-screen view is sharp.
        return (image.currentSrc || image.src || '').replace(/([?&])width=\d+/, '$1width=1946');
      }

      // Fetch (and cache) a zoom-size image ahead of display. Skipped on
      // Save-Data/2G connections, like bott-image-prefetch.
      preload(url) {
        if (!url) return null;
        const connection = navigator.connection;
        if (connection && (connection.saveData || /(^|\b)2g/.test(connection.effectiveType || ''))) return null;
        this.preloaded = this.preloaded || new Map();
        let loader = this.preloaded.get(url);
        if (!loader) {
          loader = new Image();
          loader.decoding = 'async';
          loader.src = url;
          this.preloaded.set(url, loader);
        }
        return loader;
      }

      onGalleryPointerOver(event) {
        const opener = event.target.closest('.product__modal-opener--image');
        if (!opener || !this.gallery.contains(opener)) return;
        const image = opener.querySelector('img');
        if (image) this.preload(this.zoomUrl(image));
      }

      onGalleryClick(event) {
        const opener = event.target.closest('.product__modal-opener--image');
        if (!opener || !this.gallery.contains(opener)) return;
        const image = opener.querySelector('img');
        if (!image) return;
        // Suppress Dawn's modal-opener (its button listener is downstream of
        // this capture-phase handler) and show the simple zoom instead.
        event.preventDefault();
        event.stopPropagation();
        this.lastTrigger = opener.querySelector('.product__media-toggle') || opener;
        this.open(image);
      }

      ensureOverlay() {
        if (this.overlay) return;
        const overlay = document.createElement('div');
        overlay.className = 'bott-image-zoom__overlay';
        overlay.tabIndex = -1;
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        const content = document.createElement('div');
        content.className = 'bott-image-zoom__content';
        overlay.appendChild(content);
        overlay.addEventListener('click', this.onOverlayClick);
        this.overlay = overlay;
        this.overlayContent = content;
      }

      open(sourceImage) {
        this.ensureOverlay();
        // Every image in the gallery, stacked in gallery order; variant
        // changes can reorder media, so the stack is rebuilt per open.
        const galleryImages = Array.from(this.gallery.querySelectorAll('.product__modal-opener--image img'));
        const sources = galleryImages.length ? galleryImages : [sourceImage];
        if (this.hiResObserver) this.hiResObserver.disconnect();
        this.overlayContent.textContent = '';
        let target = null;
        for (const source of sources) {
          const item = document.createElement('img');
          item.className = 'bott-image-zoom__image';
          item.decoding = 'async';
          const currentSrc = source.currentSrc || source.src || '';
          // The gallery candidate is already decoded — shows instantly.
          item.src = currentSrc;
          item.alt = source.alt || '';
          // Fix the display geometry from the source's intrinsic ratio so the
          // instant low-res preview and the hi-res swap render at the exact
          // same size (same trick as the lookbook popup — no size jump).
          const width = source.naturalWidth || source.width;
          const height = source.naturalHeight || source.height;
          if (width && height) {
            item.style.setProperty('--bott-zoom-ar', `${width} / ${height}`);
            item.classList.add('has-geometry');
          }
          const hiResSrc = this.zoomUrl(source);
          if (hiResSrc && hiResSrc !== currentSrc) item.dataset.hires = hiResSrc;
          this.overlayContent.appendChild(item);
          if (source === sourceImage) target = item;
        }
        this.overlay.setAttribute('aria-label', sourceImage.alt || this.dataset.label || '');
        document.body.appendChild(this.overlay);
        // Reserve the scrollbar's width so hiding overflow doesn't reflow/shift
        // the page in the frame before the overlay covers it.
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--bott-image-zoom-scrollbar', `${scrollbarWidth}px`);
        document.documentElement.classList.add('bott-image-zoom-open');
        requestAnimationFrame(() => this.overlay.classList.add('is-open'));
        document.addEventListener('keydown', this.onKeydown);
        this.overlay.focus({ preventScroll: true });
        this.armHiResSwaps();
        (target || this.overlayContent.firstElementChild)?.scrollIntoView({ block: 'center', behavior: 'instant' });
      }

      // Swap each stacked image to its zoom size as it nears the viewport
      // (the clicked one is in view, so it swaps first — usually straight
      // from the hover preload cache).
      armHiResSwaps() {
        this.hiResObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              this.hiResObserver.unobserve(entry.target);
              this.swapToHiRes(entry.target);
            }
          },
          { root: this.overlay, rootMargin: '150% 0px' }
        );
        for (const item of this.overlayContent.children) {
          if (item.dataset.hires) this.hiResObserver.observe(item);
        }
      }

      swapToHiRes(item) {
        const url = item.dataset.hires;
        if (!url) return;
        let loader = this.preload(url);
        if (!loader) {
          // Save-Data path: the visitor explicitly zoomed, so still load it.
          loader = new Image();
          loader.decoding = 'async';
          loader.src = url;
        }
        const apply = () => {
          if (item.isConnected) item.src = url;
        };
        const swap = () => (loader.decode ? loader.decode().then(apply, apply) : apply());
        if (loader.complete && loader.naturalWidth > 0) swap();
        else loader.addEventListener('load', swap, { once: true });
      }

      onOverlayClick() {
        this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      close(immediate) {
        if (!this.overlay || !this.overlay.isConnected) return;
        if (this.hiResObserver) this.hiResObserver.disconnect();
        document.removeEventListener('keydown', this.onKeydown);
        document.documentElement.classList.remove('bott-image-zoom-open');
        document.documentElement.style.removeProperty('--bott-image-zoom-scrollbar');
        const overlay = this.overlay;
        overlay.classList.remove('is-open');
        const remove = () => overlay.remove();
        if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          remove();
        } else {
          const done = () => {
            overlay.removeEventListener('transitionend', done);
            remove();
          };
          overlay.addEventListener('transitionend', done);
          setTimeout(done, 400); // fallback if transitionend doesn't fire
        }
        if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
          this.lastTrigger.focus();
        }
      }
    }
  );
}
